import * as React from 'react';

import { Button } from 'components/Button';
import Icon from 'components/icon';
import { TempControlCalculationMethod, TempControlProject, TempControlValveFamily } from '__generated-api__';

import ErrorList from 'tempcontrol/components/error-list';
import WizardHero from 'tempcontrol/components/wizard-hero';
import { AddOnOptionKey } from 'tempcontrol/enums';
import { useTempControlProductCards } from 'tempcontrol/hooks/use-tempcontrol-product-cards';
import ProductResultCard from 'tempcontrol/results/product-result-card';
import ResultsAccordions from 'tempcontrol/results/results-accordions';
import ShareResultsWidget from 'tempcontrol/results/share-results-widget';
import { resolveResultProducts, selectedResultPartNumber } from 'tempcontrol/utils/resolve-result-products';

interface ResultsPageProps {
  project: TempControlProject;
  selectedMethod: TempControlCalculationMethod;
  onSelectMethod: (method: TempControlCalculationMethod) => void;
  selectedValveFamily: TempControlValveFamily;
  onSelectValveFamily: (family: TempControlValveFamily) => void | Promise<void>;
  isSavingValveFamily: boolean;
  onToggleOption: (optionKey: AddOnOptionKey, checked: boolean) => void | Promise<void>;
  isUpdatingOptions: boolean;
  onEdit: () => void;
  onStartNew: () => void;
  onDownload: (prefill?: { customer_email?: string }) => void;
  isDownloading: boolean;
  calculationErrors: string[];
}

/**
 * Advisory shown inside the thermostatic (conventional) valve card only:
 * thermostatic valves may need a Hi-Lo assembly to hold temperature at low
 * demand, whereas digital valves recirculate down to 2-4 GPM and don't.
 */
const thermostaticNote = (
  <div
    style={{
      padding: '0.85rem 1rem',
      border: '1px solid #d5d8dc',
      background: 'rgba(0, 0, 0, 0.03)',
      borderRadius: '4px',
    }}
  >
    <p style={{ fontSize: '0.8125rem', margin: '0 0 0.5rem' }}>
      <strong>Note:</strong> If selecting a thermostatic valve, a TempControl Hi-Lo Thermostatic Mixing Valve and Piping
      may be necessary to maintain accurate temperatures, depending on building flow rate during low demand periods.
    </p>
    <p style={{ fontSize: '0.8125rem', margin: 0 }}>
      TempControl Digital valves have a minimum recirculation flow rate of 2-4 GPM and therefore eliminate the need for
      Hi-Lo configurations.
    </p>
  </div>
);

/**
 * Standalone (non-numbered) Results screen rendered after the wizard
 * completes. Layout follows the tempcontrol s5 cutup.
 */
export const ResultsPage: React.VFC<ResultsPageProps> = ({
  project,
  selectedMethod,
  onSelectMethod,
  selectedValveFamily,
  onSelectValveFamily,
  isSavingValveFamily,
  onToggleOption,
  isUpdatingOptions,
  onEdit,
  onStartNew,
  onDownload,
  isDownloading,
  calculationErrors,
}) => {
  const result = project.result;
  const hasResult = result !== null && typeof result !== 'undefined';
  const symmonsMethodSelected = selectedMethod === TempControlCalculationMethod.Symmons;

  const resolvedProducts = React.useMemo(
    () => (hasResult && result ? resolveResultProducts(result) : null),
    [hasResult, result]
  );

  const catalogSkus = React.useMemo(() => {
    if (!resolvedProducts) return [];
    return [resolvedProducts.recommended.catalogSku, resolvedProducts.alternate?.catalogSku].filter(
      (sku): sku is string => Boolean(sku)
    );
  }, [resolvedProducts]);

  const displayPartNumber = React.useMemo(() => {
    if (!resolvedProducts) return '—';
    return selectedResultPartNumber(resolvedProducts, selectedValveFamily);
  }, [resolvedProducts, selectedValveFamily]);

  // Valve family the shown part number belongs to, labelled for the
  // "Show all data" list ("Valve Type" — Digital vs Conventional).
  const valveType = selectedValveFamily === TempControlValveFamily.Digital ? 'Digital' : 'Conventional';

  // A manifolded result means N identical valves in parallel. `manifold_count`
  // is the manifold size (0 = single valve), so total valves = count + 1.
  const valveQuantity = React.useMemo(() => {
    const manifolds = result?.manifold_count ?? 0;
    return manifolds > 0 ? manifolds + 1 : 1;
  }, [result]);

  const handleSelectFamily = React.useCallback(
    (family: TempControlValveFamily) => {
      if (family === selectedValveFamily || isSavingValveFamily) return;
      void onSelectValveFamily(family);
    },
    [isSavingValveFamily, onSelectValveFamily, selectedValveFamily]
  );

  const { cards, isLoading: isLoadingProducts, error: productLoadError } = useTempControlProductCards(catalogSkus);

  return (
    <>
      <WizardHero
        rightSlot={
          <Button variant="white" onClick={onStartNew} leftIcon="add-thin">
            New Project
          </Button>
        }
      />

      <section className="c-block c-block--spacing-t-extra-small c-block--spacing-t@lg c-block--spacing-b-small c-block--spacing-b@lg u-overflow-hidden">
        <div className="o-container-fluid">
          <div className="o-row u-relative">
            <div className="o-col-12 o-col-5@md o-col-4@lg u-pr-0@xl u-static u-relative@md">
              <button
                type="button"
                className="c-link-cta-ghost c-link-cta--small c-link-basic u-mb-spacer-base-small"
                onClick={onEdit}
              >
                <Icon name="pencil" className="o-svg-icon o-svg-larger" />
                <span>Edit Project</span>
              </button>

              <div className="c-metrics-goal c-metrics-goal--simple u-mb-spacer-base-large" aria-hidden="true">
                <div className="c-metrics-goal__bar">
                  <div className="c-metrics-goal__progress" style={{ width: '100%' }} />
                </div>
              </div>

              <div className="c-block__header">
                <p className="c-headline u-text-primary">Your Results For</p>
                <h3 className="c-title">{project.project_name}</h3>
                <p>
                  The following Symmons mixing valve is recommended for your project, based on the information provided.
                </p>
                <p>To revise your project, click Edit Project to adjust your inputs and calculate new results.</p>
              </div>

              <ShareResultsWidget
                className="c-widget c-widget--outline u-mt-spacer-base u-mt-spacer-base-large@lg u-mb-spacer-base u-hidden u-block@md"
                projectKey={project.key}
                onRequestDownload={(email) => onDownload(email ? { customer_email: email } : undefined)}
              />

              <div className="c-block__decoration-circle" aria-hidden="true" />
            </div>

            <div className="o-col-12 o-col-7@md o-col-8@lg u-pl-spacer-section-small@xl">
              <div className="c-list-grid c-list-grid--small u-items-start">
                {/* Symmons calculation method isn't available yet, so its toggle is hidden for now.
                    State + handlers are left intact; delete the `false &&` guard to bring it back. */}
                {false && (
                  <div className="u-flex-grow u-mb-spacer-base-small">
                    <div className="c-form-element c-form-element--style-line c-form-element--toggle c-form-element--large u-mb-0">
                      <div className="c-form-element__field">
                        <input
                          id="tempcontrol-method-toggle"
                          type="checkbox"
                          checked={symmonsMethodSelected}
                          onChange={() =>
                            onSelectMethod(
                              symmonsMethodSelected
                                ? TempControlCalculationMethod.Hunters
                                : TempControlCalculationMethod.Symmons
                            )
                          }
                          disabled
                          title="Symmons method support is coming soon."
                        />
                        <label htmlFor="tempcontrol-method-toggle" className="u-text-sm u-pt-spacer">
                          Switch to Symmons method
                        </label>
                      </div>
                    </div>
                  </div>
                )}
                <div className="u-mb-spacer-base-small">
                  <a href="#about-method" className="c-link-cta-basic c-link-cta--small">
                    <span>Find Out More About Our Method</span>
                    <Icon name="info" className="o-svg-icon o-svg-large u-text-secondary" />
                  </a>
                </div>
              </div>

              {calculationErrors.length > 0 && (
                <div className="u-mb-spacer-base">
                  <ErrorList title="The engine couldn't run yet:" messages={calculationErrors} />
                </div>
              )}

              {hasResult && result?.message && (
                <div
                  role="alert"
                  className="u-mb-spacer-base"
                  style={{
                    padding: '1rem 1.25rem',
                    border: '1px solid #b9770e',
                    background: 'rgba(214, 137, 16, 0.07)',
                    borderRadius: '4px',
                  }}
                >
                  <strong style={{ display: 'block', marginBottom: '0.4rem' }}>Nearest Match</strong>
                  <p style={{ margin: 0 }}>
                    We didn’t find an exact match based on your selections. To refine the results, click{' '}
                    <button
                      type="button"
                      onClick={onEdit}
                      style={{
                        background: 'none',
                        border: 0,
                        padding: 0,
                        font: 'inherit',
                        color: 'inherit',
                        fontWeight: 600,
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      }}
                    >
                      Edit Project
                    </button>{' '}
                    and revise your entry information.
                  </p>
                  <p style={{ margin: '0.75rem 0 0' }}>
                    Need help? Just call our Symmons Technical Support Team at <strong>1-800-SYMMONS</strong> to help
                    you determine the correct valve for your project.
                  </p>
                </div>
              )}

              {hasResult && result && resolvedProducts && (
                <div>
                  <ProductResultCard
                    headline="Recommended For Your Project"
                    slot={resolvedProducts.recommended}
                    card={cards[resolvedProducts.recommended.catalogSku]}
                    isLoading={isLoadingProducts}
                    valveQuantity={valveQuantity}
                    note={
                      resolvedProducts.recommended.family === TempControlValveFamily.Conventional
                        ? thermostaticNote
                        : undefined
                    }
                    isSelected={
                      resolvedProducts.digital
                        ? selectedValveFamily === TempControlValveFamily.Digital
                        : selectedValveFamily === TempControlValveFamily.Conventional
                    }
                    onSelect={() => handleSelectFamily(resolvedProducts.recommended.family)}
                  />

                  {resolvedProducts.alternate && (
                    <ProductResultCard
                      headline={resolvedProducts.alternateHeadline}
                      slot={resolvedProducts.alternate}
                      card={cards[resolvedProducts.alternate.catalogSku]}
                      isLoading={isLoadingProducts}
                      valveQuantity={valveQuantity}
                      note={
                        resolvedProducts.alternate.family === TempControlValveFamily.Conventional
                          ? thermostaticNote
                          : undefined
                      }
                      isSelected={selectedValveFamily === TempControlValveFamily.Conventional}
                      onSelect={() => handleSelectFamily(TempControlValveFamily.Conventional)}
                    />
                  )}

                  {productLoadError && <p className="c-note u-mb-spacer-base">{productLoadError}</p>}
                </div>
              )}

              <ShareResultsWidget
                className="c-widget c-widget--divider u-mt-spacer-base u-mt-spacer-base-large@lg u-pt-spacer-base u-hidden@md"
                projectKey={project.key}
                onRequestDownload={(email) => onDownload(email ? { customer_email: email } : undefined)}
              />

              {hasResult && result && (
                <ResultsAccordions
                  project={project}
                  result={result}
                  selectedMethod={selectedMethod}
                  displayPartNumber={displayPartNumber}
                  valveType={valveType}
                  addOnsApply={selectedValveFamily === TempControlValveFamily.Conventional}
                  onToggleOption={onToggleOption}
                  isUpdatingOptions={isUpdatingOptions}
                  onDownload={() => onDownload()}
                  isDownloading={isDownloading}
                />
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ResultsPage;
